import { AutocompleteChangeDetails, AutocompleteChangeReason } from "@mui/material";
import React, { forwardRef, Ref, useEffect, useImperativeHandle, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ResourceTypeEnum } from "../../common/enums/ResourceTypeEnum";
import { IeLynxObject } from "../../common/models/eLynxObject";
import { IAttachment } from "../../common/models/IAttachment";
import { getAttachmentUrl } from "../../common/utilities/AttachmentHelpers";
import { useConfirmation } from "../../contexts/confirmation-context/ConfirmationContext";
import { useOfflineDetectContext } from "../../contexts/offline-detect-context/UseOfflineDetectContext";
import { useUserContext } from "../../contexts/user-context/UserContext";
import { useRouteContext } from "../../hooks/use-route-context/UseRouteContext";
import { useObjectViewState } from "../../queries/object-view-state/UseObjectViewState";
import { useDeleteRunTicket } from "../../queries/run-tickets/UseDeleteRunTicket";
import { useRunTickets } from "../../queries/run-tickets/UseRunTickets";
import { useQueryNotifications } from "../../queries/UseQueryNotifications";
import { IRunTicketModel } from "../../services/run-tickets-data-service/Models";
import { BaseActionEnum } from "../action-menu/ActionMenuItem";
import { ContentContainer } from "../content-container/ContentContainer";
import { ContentSection } from "../content-section/ContentSection";
import { ViewOptionStateEnum } from "../contextual-app-bar/ViewOptionStateEnum";
import FileDownloader, { FileDownloaderType } from "../file-downloader/FileDownloader";
import FileViewerDialog, { bindFileViewerDialog, useFileViewerDialogState } from "../file-viewer-dialog/FileViewerDialog";
import LoadingZone from "../loading-zone/LoadingZone";
import NoData from "../no-data/NoData";
import OptionsSideSheet from "../options-side-sheet/OptionsSideSheet";
import RunTicketEditDialog, { bindRunTicketEditDialog, useRunTicketEditDialogState } from "../run-ticket-edit-dialog/RunTicketEditDialog";
import SideSheetDatePicker from "../side-sheet/side-sheet-date-picker/SideSheetDatePicker";
import SideSheetMultiSelect from "../side-sheet/side-sheet-multiselect/SideSheetMultiSelect";
import VirtualizedList, { VirtualizedListItem } from "../virtualized-list/VirtualizedList";
import { RunTicketActionEnum, RunTicketActionPayloadType, RunTicketListItem } from "./RunTicketListItem";
import useRunTicketListState, { allTanksOption, ALL_TANKS_OPTION_ID, initialState, TankOption } from "./RunTicketListState";
import { ApplicationMimeTypesEnum } from "../../common/enums/ApplicationMimeTypesEnum";
import UUID from "../../common/utilities/Uuid";
import { useProgressContext } from "../../contexts/progress-context/ProgressContext";
import GaugeSheetDataService from "../../services/gauge-sheet-data-service/GaugeSheetDataService";
import { RestError } from "../../services/rest-service/RestService";
import { AppNotificationTypeEnum } from "../../contexts/app-notification-context/AppNotificationContextTypes";
import { useAppNotificationContext } from "../../contexts/app-notification-context/UseAppNotificationContext";
import { ActionTypeEnum, StatusEnum } from "../../common/IExportNotificationMsg";

export type RunTicket = IRunTicketModel & VirtualizedListItem;

export type RunTicketListProps = {
    visible?: boolean;
    setViewOptionState?: (state: ViewOptionStateEnum) => void;
};

export type RunTicketListType = {
    executeAction: (action: BaseActionEnum | BaseActionEnum) => void;
};

function RunTicketList({
    visible,
    setViewOptionState
}: RunTicketListProps, ref: Ref<unknown> | undefined) {
    const routeContext = useRouteContext();
    const [userState] = useUserContext();
    const [offlineState] = useOfflineDetectContext();
    const { t } = useTranslation();
    const runTicketDialog = useRunTicketEditDialogState();
    // const newRunTicketIndex = useRef<number>(-1);
    const fileViewerDialog = useFileViewerDialogState();
    const fileDownloaderRef = useRef<FileDownloaderType>(null);
    const [expansionState, setExpansionState] = useState<{ [key: number]: boolean; }>({});
    const { confirm, acknowledge } = useConfirmation();
    const [progressState, actionStarted] = useProgressContext();
    const [,setAppNotificationState] = useAppNotificationContext();

    const {
        state,
        resetOptions,
        setTanks,
        setTimestamp,
        setDownloadUid,
    } = useRunTicketListState();

    useImperativeHandle<unknown, RunTicketListType>(
        ref,
        () => ({
            executeAction: executeAction
        })
    );

    const { data: objectViewState } = useObjectViewState({
        customerID: userState.customer?.id as number,
        groupID: routeContext.groupID as number,
        contextObject: routeContext.contextObject,
        offline: offlineState.offline
    });

    const objectName = objectViewState?.contextObject.objectName ?? "";

    const { data, status: getStatus, refetch } = useQueryNotifications(useRunTickets)({
        customerID: userState.customer?.id as number,
        groupID: routeContext.groupID as number,
        contextObject: routeContext.contextObject as IeLynxObject,
        offline: offlineState.offline,
        timestamp: state.timestamp,
        options: {
            tankIDs: state.tanks
                ?.filter(tank => tank.id !== ALL_TANKS_OPTION_ID)
                ?.map(tank => tank.id)
        },
        enabled: visible
    });

    const { mutate: deleteRunTicket } = useQueryNotifications(useDeleteRunTicket)({
        customerID: userState.customer?.id as number,
        groupID: routeContext.groupID as number,
        contextObject: routeContext.contextObject as IeLynxObject,
        timestamp: state.timestamp,
        offline: offlineState.offline,
        onSuccess: () => {
            acknowledge(t("deleteSuccessMessage", { itemName: t("dataTypeNames.runTicket") }));
        }
    });

    useEffect(() => {
        if (visible) {
            const viewOptionState = state.isOptionsDirty ? ViewOptionStateEnum.Changed : ViewOptionStateEnum.Default;
            setViewOptionState && setViewOptionState(viewOptionState);
        }
    }, [
        visible,
        state.isOptionsDirty,
        setViewOptionState
    ]);

    const runTickets = data?.runTickets ?? new Array<IRunTicketModel>();
    const runTicketTanks = data?.tanks ?? new Array<IRunTicketModel.Tank>();
    const tankOptions = [
        allTanksOption,
        ...runTicketTanks.map(tank => {
            return {
                id: tank.id,
                displayText: tank.displayText
            };
        })
    ];

    async function createRunTicket() {
        const defaultTimestamp = new Date().toISOString();

        const newRunTicket = {
            id: -1, //offlineState.offline ? await OfflineDataService.getNewRunTicketID(userState.customer?.id as number) : newRunTicketIndex.current--,
            tank: {
                id: routeContext.tankID ?? -1,
                displayText: "",
                isDualPhase: false,
                resourceType: ResourceTypeEnum.Oil,
                tankStyle: {
                    id: -1,
                    inchesMultiplier: 0,
                    heightInInches: 144,
                    totalBarrels: 0,
                }
            },
            createdDateTime: defaultTimestamp,
            creator: "",
            ticketNumber: "",
            purchaserName: undefined,
            comments: undefined,

            startDateTime: defaultTimestamp,
            tankLevelOpen: undefined,
            interfaceLevelOpen: undefined,
            temperatureTop: undefined,
            sealOff: undefined,

            completeDateTime: defaultTimestamp,
            tankLevelClose: undefined,
            interfaceLevelClose: undefined,
            temperatureBottom: undefined,
            sealOn: undefined,

            specificGravity: undefined,
            basicSedimentsAndWater: undefined,

            attachments: new Array<IAttachment>()

            //tankLevelEstimatedHaul: number;
            //interfaceLevelEstimatedHaul: number;
        };

        await runTicketDialog.open({
            runTicket: newRunTicket,
            timestamp: state.timestamp,
            forTank: (routeContext.tankID ?? -1) > -1
        });

    }

    async function getDeleteRunTicketConfirmation() {

        return (await confirm({
            dialogTitle: t("deleteConfirmation.title", { itemName: t("dataTypeNames.runTicket") }),
            confirmationText: t("deleteConfirmation.confirmationText"),
            yesButtonText: t("deleteConfirmation.yesButtonText"),
            noButtonText: t("deleteConfirmation.noButtonText"),
            showConfirmationText: true,
            showYesButton: true,
            showNoButton: true,
            showCloseButton: true
        })) === "yes";
    }

    async function confirmAndDeleteRunTicket(runTicket: RunTicket) {
        const canDeleteRunTicket = await getDeleteRunTicketConfirmation();

        if (canDeleteRunTicket) {
            deleteRunTicket(runTicket);
        }
    }

    async function onRunTicketAction(action: RunTicketActionEnum, payload: RunTicketActionPayloadType) {

        switch (action) {
            case RunTicketActionEnum.Edit:
                editRunTicket(payload as RunTicket);
                break;
            case RunTicketActionEnum.Delete:
                confirmAndDeleteRunTicket(payload as RunTicket);
                break;
            case RunTicketActionEnum.ToggleExpansion:
                setExpansionState(prev => {
                    const newState = { ...prev };
                    if (newState[payload.id]) {
                        delete newState[payload.id];
                    } else {
                        newState[payload.id] = !newState[payload.id];
                    }
                    return newState;
                });
                break;
            case RunTicketActionEnum.ViewAttachment: {
                const attachment = payload as IAttachment;

                await fileViewerDialog.open({
                    title: attachment.description,
                    url: getUrl(attachment.base64Data, attachment.url),
                    mimeType: attachment.mimeType
                });
                // console.log(`viewing ${attachment.fileName}`);
                break;
            }

            case RunTicketActionEnum.DownloadAttachment: {
                const attachment = payload as IAttachment;
                fileDownloaderRef.current?.download(
                    getAttachmentUrl(attachment.base64Data, attachment.url),
                    attachment.fileName);
            }
        }

    }

    function getUrl(base64Data: string | undefined, url: string | undefined): string | undefined {
        return base64Data
            ? `data:image/jpeg;base64,${base64Data}`
            : url;
    }

    async function editRunTicket(runTicket: RunTicket) {

        await runTicketDialog.open({
            runTicket: runTicket,
            timestamp: state.timestamp
        });
    }


    function onExport(mimeType: ApplicationMimeTypesEnum)  {
        const uid = UUID.generate();
        setDownloadUid(uid);

        actionStarted({
            uid: uid,
            objectID: routeContext.contextObject!.id,
            objectType: routeContext.contextObject!.objectType,
            title: 'Run Tickets', 
            notificationMsg: {
                action: ActionTypeEnum.Download,
                notificationID: uid,
                status: StatusEnum.Started,
                statusMessage: t('progress.sendingRequest'),
                additionalInfo: '',
                blobURI: '',
                percentComplete: 0
            }
        });
        
        try {

            GaugeSheetDataService.export(routeContext.groupID as number, 
                routeContext.deviceID ?? -1,
                state.timestamp,
                uid,
                mimeType,
                !routeContext.deviceID ? objectViewState?.linkedDeviceIDs : undefined);

        } catch(error) {
            const restError = error as RestError;
            setAppNotificationState({
                notificationType: AppNotificationTypeEnum.Error,
                show: true,
                error: restError
            });
        }
    }


    function renderItem(_index: number, item: RunTicket) {
        return (
            <RunTicketListItem
                key={`${item.id}`}
                item={item}
                onAction={onRunTicketAction}
                expanded={expansionState[item.id] ?? false}
            />
        );
    }

    const noDataMessage = data?.totalCount === 0
        ? t("noData.noDataTypesForObjectForPeriod", { dataTypeName: t("dataTypeNames.runTicket"), objectName })
        : data?.filteredCount === 0
            ? t("noData.noFilteredDataTypesForObject", { dataTypeName: t("dataTypeNames.runTicket"), objectName })
            : undefined;

    function executeAction(action: BaseActionEnum | BaseActionEnum) {

        switch (action) {
            case BaseActionEnum.Refresh:
                refetch();
                break;

            case BaseActionEnum.AddRunTicket:
                createRunTicket();
                break;

            case BaseActionEnum.Excel:
                onExport(ApplicationMimeTypesEnum.MSExcel2007);
                    break;
    
                case BaseActionEnum.PDF: 
                onExport(ApplicationMimeTypesEnum.PDF)
                    break;
     
        }
    }

    function onTankOptionsChanged(
        event: React.SyntheticEvent,
        value: TankOption[],
        reason: AutocompleteChangeReason,
        details?: AutocompleteChangeDetails<TankOption> | undefined
    ) {
        const item = tankOptions.find(o => o.id === details?.option.id);

        if (item) {

            switch (reason) {
                case 'selectOption':

                    if (item.id === ALL_TANKS_OPTION_ID) {
                        setTanks([allTanksOption]);
                    } else {
                        setTanks(value.filter(tank => tank.id !== ALL_TANKS_OPTION_ID));
                    }
                    break;

                case 'removeOption':

                    setTanks(value.length === 0 ? [allTanksOption] : value);
                    break;

                default:
                    break;
            }
        }
    }

    // console.log('rendering runtickets');

    const progress = progressState.items.find(o => o.objectID === routeContext.contextObject!.id && o.objectType === routeContext.contextObject!.objectType); 

    useEffect(() => {

        if (progress?.notificationMsg.status === StatusEnum.Completed ||
            progress?.notificationMsg.status === StatusEnum.Failed) {
            setDownloadUid(undefined);
        }
    }, [
        routeContext.groupID,
        progress,
        state.downloadUid,
        setDownloadUid
    ]);


    return (
        <>
            {visible &&
                <>
                    <LoadingZone isLoading={getStatus === "loading"}>
                        <ContentContainer>
                            <ContentSection>
                                {getStatus === "success" && noDataMessage &&
                                    <NoData text={noDataMessage} />
                                }

                                <VirtualizedList
                                    items={runTickets}
                                    renderItem={renderItem}
                                />
                            </ContentSection>

                            <OptionsSideSheet onReset={() => resetOptions()}>
                                <SideSheetMultiSelect<TankOption>
                                    id="filter-tanks"
                                    label={t("runTicketFilter.selectTanks")}
                                    options={tankOptions}
                                    isOptionEqualToValue={(option, value) => option.id === value.id}
                                    value={state.tanks}
                                    getOptionLabel={(option) => option.displayText}
                                    onChange={onTankOptionsChanged}
                                />

                                <SideSheetDatePicker
                                    label={t("runTicketFilter.selectMonth")}
                                    value={state.timestamp}
                                    onAccept={(value) => {
                                        setTimestamp(value ?? initialState.timestamp);
                                    }}
                                    views={["month", "year"]}
                                />
                            </OptionsSideSheet>
                        </ContentContainer>
                    </LoadingZone>

                    <FileDownloader ref={fileDownloaderRef} />
                    <RunTicketEditDialog {...bindRunTicketEditDialog(runTicketDialog)} />
                    <FileViewerDialog {...bindFileViewerDialog(fileViewerDialog)} />
                </>
            }
        </>
    );
}

export default forwardRef(RunTicketList);